// =============================================================================
// DiarioView.ts — Diário Oficial (#99): a lista das Promessas de Campanha
// (conquistas) com feitas/pendentes, barra de progresso e o que cada uma
// destrava (votos vitalícios, armas do pool da urna e hooks de skin #24).
// Avaliação e persistência são do servidor; aqui é só a edição impressa.
// Desde a issue #122 vive como aba da tela CONQUISTAS E ARQUIVOS, paginada.
// =============================================================================
import {
  CONQUISTA_LIST, type ConquistaDef, type FeitosState,
} from '../../../shared/feitos';
import { WEAPON_TABLE } from '../../../shared/weapons';
import { fetchPerfil } from '../../infrastructure/MetaClient';
import { audio } from '../AudioEngine';
import { $, renderPager } from './dom';
import { seloIcon } from './pixelIcons';

const POR_PAGINA = 10;

const fmt = (n: number): string => n.toLocaleString('pt-BR');

// Progresso legível: metas de tempo em minutos, o resto em número cru
function progresso(c: ConquistaDef, atual: number): string {
  if (c.formato === 'tempo') {
    return `${Math.floor(Math.min(atual, c.alvo) / 60)}/${Math.round(c.alvo / 60)} min`;
  }
  return `${fmt(Math.min(atual, c.alvo))}/${fmt(c.alvo)}`;
}

// O que a promessa paga quando cumprida (votos sempre; arma e skin quando houver)
function recompensa(c: ConquistaDef): string {
  const partes = [`+${fmt(c.votos)} votos vitalícios`];
  if (c.arma) partes.push(`destrava ${WEAPON_TABLE[c.arma]?.nome ?? c.arma} na urna`);
  if (c.skin) partes.push('destrava skin (em breve)');
  return partes.join(' · ');
}

export class DiarioView {
  private feitos: FeitosState | null = null;
  private page = 0;

  show(): void {
    this.page = 0;
    $('diario-lista').innerHTML = '<p class="zona-vazia">Imprimindo a edição de hoje...</p>';
    $('diario-pager').innerHTML = '';
    void this.load();
  }

  private async load(): Promise<void> {
    const res = await fetchPerfil();
    if (!res.ok || !res.feitos) {
      $('diario-lista').innerHTML =
        '<p class="zona-vazia">A gráfica do Diário Oficial está em greve. Tente de novo.</p>';
      return;
    }
    this.feitos = res.feitos;
    this.render();
  }

  private render(): void {
    const feitos = this.feitos!;
    const feitas = new Set(feitos.conquistas);
    const total = CONQUISTA_LIST.length;
    const done = CONQUISTA_LIST.filter((c) => feitas.has(c.id)).length;
    $('diario-resumo').textContent =
      `${done}/${total} promessas cumpridas — número recorde na história da República`;

    const box = $('diario-lista');
    box.innerHTML = '';
    // Cumpridas por último: a pendência é o que ocupa a mente (e a manchete)
    const ordenadas = [...CONQUISTA_LIST].sort((a, b) =>
      Number(feitas.has(a.id)) - Number(feitas.has(b.id)));
    // Edição paginada (issue #122): 10 promessas por página
    const pagina = ordenadas.slice(this.page * POR_PAGINA, (this.page + 1) * POR_PAGINA);
    for (const c of pagina) {
      const cumprida = feitas.has(c.id);
      const atual = cumprida ? c.alvo : c.progress(feitos.stats);
      const pct = Math.min(100, (atual / c.alvo) * 100);
      const row = document.createElement('div');
      row.className = `diario-item${cumprida ? ' cumprida' : ''}`;
      row.innerHTML = `
        <span class="diario-selo">${seloIcon(26)}</span>
        <div class="diario-info">
          <div class="diario-nome">${c.nome}
            ${cumprida ? '<span class="diario-carimbo">CUMPRIDA</span>' : ''}</div>
          <p class="diario-desc">${c.desc}</p>
          <div class="diario-barra"><div style="width:${pct}%"></div></div>
          <div class="diario-rodape">
            <span class="diario-prog">${progresso(c, atual)}</span>
            <span class="diario-premio">${recompensa(c)}</span>
          </div>
        </div>`;
      box.appendChild(row);
    }
    renderPager($('diario-pager'), ordenadas.length, this.page, POR_PAGINA, (p) => {
      audio.sfx.urnaKey();
      this.page = p;
      this.render();
    });
  }
}
